import { useState, useEffect, useCallback } from "react";

// react-router components

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Data
import photosData, { allPhotos } from "pages/Home/sections/data/photosData";

function PhotoGalleryPreview() {
  const [open, setOpen] = useState(false);
  const [activePhoto, setActivePhoto] = useState(null);

  // Pull featured from property first, then fill with beaches + activities
  const allFeatured = [
    ...photosData.property.photos.filter((p) => p.featured),
    ...photosData.beaches.photos.filter((p) => p.featured),
    ...photosData.activities.photos.filter((p) => p.featured),
  ].slice(0, 6);

  // Front yard (first featured) is the big gallery image
  const hero = allFeatured[0];
  const rest = allFeatured.slice(1);

  const handleOpen = (photo) => {
    setActivePhoto(photo);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setActivePhoto(null);
  };

  const handleNav = useCallback(
    (direction) => {
      const currentIndex = allPhotos.indexOf(activePhoto);
      const nextIndex = (currentIndex + direction + allPhotos.length) % allPhotos.length;
      setActivePhoto(allPhotos[nextIndex]);
    },
    [activePhoto]
  );

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") handleNav(-1);
      else if (e.key === "ArrowRight") handleNav(1);
      else if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, handleNav]);

  const clickableSx = {
    cursor: "pointer",
    "&:hover": { opacity: 0.85 },
    transition: "opacity 0.2s",
  };

  return (
    <MKBox component="section" py={6}>
      <Container maxWidth="xl">
        <MKTypography variant="h3" mb={3}>
          Photo Gallery
        </MKTypography>
        <Grid container spacing={2}>
          {/* Large hero image */}
          <Grid item xs={12} md={8}>
            <MKBox
              component="img"
              src={hero.src}
              alt={hero.alt}
              width="100%"
              borderRadius="xl"
              shadow="md"
              onClick={() => handleOpen(hero)}
              sx={{
                aspectRatio: "16 / 9",
                objectFit: "cover",
                objectPosition: "50% 75%",
                ...clickableSx,
              }}
            />
          </Grid>
          {/* Two stacked images — desktop only */}
          <Grid item md={4} sx={{ display: { xs: "none", md: "block" } }}>
            <Grid container spacing={2}>
              {rest.slice(0, 2).map((photo) => (
                <Grid item xs={12} key={photo.alt}>
                  <MKBox
                    component="img"
                    src={photo.src}
                    alt={photo.alt}
                    width="100%"
                    borderRadius="xl"
                    shadow="md"
                    onClick={() => handleOpen(photo)}
                    sx={{ aspectRatio: "16 / 8.65", objectFit: "cover", ...clickableSx }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
          {/* Three equal images below — desktop only */}
          {rest.slice(2, 5).map((photo, i) => (
            <Grid item md={4} key={photo.alt} sx={{ display: { xs: "none", md: "block" } }}>
              <MKBox position="relative" overflow="hidden" borderRadius="xl">
                <MKBox
                  component="img"
                  src={photo.src}
                  alt={photo.alt}
                  width="100%"
                  shadow="md"
                  onClick={() => handleOpen(photo)}
                  sx={{
                    aspectRatio: "16 / 9",
                    objectFit: "cover",
                    display: "block",
                    ...clickableSx,
                  }}
                />
                {i === 2 && allPhotos.length > 6 && (
                  <MKBox
                    onClick={() => handleOpen(photo)}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: "rgba(0,0,0,0.45)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <MKTypography variant="h5" sx={{ color: "#fff" }}>
                      +{allPhotos.length - 6} more
                    </MKTypography>
                  </MKBox>
                )}
              </MKBox>
            </Grid>
          ))}
          {/* 2x2 thumbnail grid — mobile only */}
          {rest.slice(0, 4).map((photo, i) => (
            <Grid
              item
              xs={6}
              key={`mobile-${photo.alt}`}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              <MKBox position="relative" overflow="hidden" borderRadius="lg">
                <MKBox
                  component="img"
                  src={photo.src}
                  alt={photo.alt}
                  width="100%"
                  shadow="md"
                  onClick={() => handleOpen(photo)}
                  sx={{
                    aspectRatio: "1 / 1",
                    objectFit: "cover",
                    display: "block",
                    ...clickableSx,
                  }}
                />
                {i === 3 && allPhotos.length > 5 && (
                  <MKBox
                    onClick={() => handleOpen(photo)}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "lg",
                      bgcolor: "rgba(0,0,0,0.45)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <MKTypography variant="h6" sx={{ color: "#fff" }}>
                      +{allPhotos.length - 5} more
                    </MKTypography>
                  </MKBox>
                )}
              </MKBox>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Lightbox Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.95)" } }}
      >
        {activePhoto && (
          <MKBox display="flex" flexDirection="column" height="100%">
            <IconButton
              onClick={handleClose}
              sx={{ position: "absolute", top: 8, right: 8, color: "#fff !important", zIndex: 2 }}
            >
              <Icon>close</Icon>
            </IconButton>
            <MKBox
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              overflow="hidden"
              px={{ xs: 5, md: 8 }}
              py={2}
            >
              <IconButton
                onClick={() => handleNav(-1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: { xs: 0, md: 8 },
                  transform: "translateY(-50%)",
                  color: "#fff !important",
                  zIndex: 2,
                }}
              >
                <Icon sx={{ fontSize: "48px !important" }}>chevron_left</Icon>
              </IconButton>
              <IconButton
                onClick={() => handleNav(1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: { xs: 0, md: 8 },
                  transform: "translateY(-50%)",
                  color: "#fff !important",
                  zIndex: 2,
                }}
              >
                <Icon sx={{ fontSize: "48px !important" }}>chevron_right</Icon>
              </IconButton>
              <MKBox
                component="img"
                src={activePhoto.src}
                alt={activePhoto.alt}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </MKBox>
            <MKBox px={3} py={1.5} textAlign="center">
              <MKTypography variant="body2" color="white" fontWeight="bold">
                {activePhoto.alt}
              </MKTypography>
              {activePhoto.description && (
                <MKTypography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }} mt={0.5}>
                  {activePhoto.description}
                </MKTypography>
              )}
            </MKBox>
          </MKBox>
        )}
      </Dialog>
    </MKBox>
  );
}

export default PhotoGalleryPreview;
